import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  reapplyPreapproval,
  type PreapprovalApplication,
  type PreapprovalApplicationStatus,
} from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import {
  getBondSubmission,
  getResolvedBondConfig,
  type BondStep,
} from "@/lib/brokerBonds";
import {
  applicationStatusLabel,
  reapplyRefusal,
} from "@/lib/preapprovalApplication";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S324 — the current pre-approval application's line on the Surety
// Program panel: its status, its date, and the one action its state allows.
// A child of `SuretyProgramBlock` behind its own optional prop, so the panel's
// other additions (BRK-S329's capacity request) stay independent of it.
// Re-apply navigates only from the server's 2xx (NN #7).

export function PreapprovalApplicationStatus({
  accountId,
  application,
}: {
  accountId: string;
  application: PreapprovalApplication & {
    status: PreapprovalApplicationStatus;
  };
}) {
  const { authedRequest } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{
    message: string;
    openOrderId: string | null;
  } | null>(null);
  const { status, orderId } = application;

  async function reapply() {
    setBusy(true);
    setError(null);
    try {
      const fresh = await reapplyPreapproval(authedRequest, accountId);
      navigate(`/surety/submissions/${encodeURIComponent(fresh.id)}/continue`);
    } catch (err) {
      setError(reapplyRefusal(err));
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="preapproval-application"
      className="flex w-full min-w-0 flex-col gap-2 rounded-md border border-brk-border p-3"
    >
      <p className="text-sm text-brk-text">
        <span className="text-brk-dim">Pre-approval application: </span>
        <span className="font-medium">{applicationStatusLabel(status)}</span>
      </p>
      {application.updatedAt && (
        <p className="text-sm text-brk-dim">
          Updated {formatBondDate(application.updatedAt)}
        </p>
      )}
      {status === "draft" && application.canEdit && (
        <>
          <Link
            to={`/surety/submissions/${encodeURIComponent(orderId)}/continue`}
            className={`self-start ${buttonClass()}`}
          >
            Continue
          </Link>
          <CompletedStepPencils orderId={orderId} />
        </>
      )}
      {status === "not_granted" && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {application.canReapply && (
            <button
              type="button"
              onClick={() => void reapply()}
              disabled={busy}
              className={buttonClass()}
            >
              {busy ? "Starting…" : "Re-apply"}
            </button>
          )}
          {/* The refused application stays on the record. */}
          <Link
            to={`/surety/submissions/${encodeURIComponent(orderId)}`}
            className="text-sm font-medium text-brk-accent hover:underline"
          >
            View the application
          </Link>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error.message}
          {error.openOrderId && (
            <>
              {" "}
              <Link
                to={`/surety/submissions/${encodeURIComponent(error.openOrderId)}`}
                className="font-medium text-brk-accent hover:underline"
              >
                View the open application
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * BRK-S197 — a pencil beside each COMPLETED step of a draft application,
 * opening the wizard at that step (`/continue?step=<key>`). Completed = a
 * fields step the order holds values for. Read from the order and its
 * configuration; a failed read shows no pencils rather than an error — the
 * Continue above still reaches every step.
 */
function CompletedStepPencils({ orderId }: { orderId: string }) {
  const { authedRequest } = useAuth();
  const [steps, setSteps] = useState<BondStep[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const order = await getBondSubmission(authedRequest, orderId);
        if (order.status !== "draft") return;
        const config = await getResolvedBondConfig(
          authedRequest,
          order.subproductId,
        );
        const done = config.steps.filter((step) => {
          if (step.type !== "fields") return false;
          const bag = order.values?.[step.key];
          return bag !== undefined && Object.keys(bag).length > 0;
        });
        if (!cancelled) setSteps(done);
      } catch {
        if (!cancelled) setSteps([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, orderId]);

  if (steps.length === 0) return null;
  return (
    <ul aria-label="Completed steps" className="flex flex-col gap-1">
      {steps.map((step) => (
        <li key={step.key} className="flex min-w-0 items-center gap-2 text-sm">
          <span className="min-w-0 break-words text-brk-text">
            {step.title}
          </span>
          <Link
            to={`/surety/submissions/${encodeURIComponent(
              orderId,
            )}/continue?step=${encodeURIComponent(step.key)}`}
            aria-label={`Edit ${step.title}`}
            title={`Edit ${step.title}`}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-brk-accent hover:bg-brk-bg"
          >
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </Link>
        </li>
      ))}
    </ul>
  );
}
